/* Collection "View as" toggle.

   Grid is the default. List view reuses the horizontal card layout that
   product-card.css already defines, so this only flips classes — there is no
   list-specific CSS beyond the single-column grid rule in collection.css.
   The choice is remembered per browser. */
(() => {
  const KEY = 'vf:collection-view';
  // localStorage throws outright in some privacy modes, so neither direction
  // is allowed to take the page down with it.
  const read = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
  const write = (v) => { try { localStorage.setItem(KEY, v); } catch { /* not persisted */ } };

  document.querySelectorAll('.collection').forEach((root) => {
    const buttons = root.querySelectorAll('.collection__view-btn');
    if (!buttons.length) return;

    const apply = (view) => {
      root.dataset.view = view;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
      root.querySelectorAll('.product-card')
        .forEach((card) => card.classList.toggle('product-card--horizontal', view === 'list'));
    };

    buttons.forEach((b) => b.addEventListener('click', () => {
      apply(b.dataset.view);
      write(b.dataset.view);
    }));

    apply(read() === 'list' ? 'list' : 'grid');
  });

  /* Price slider: two range inputs on one track. They mirror the number inputs,
     which are what the form actually submits. */
  document.querySelectorAll('[data-price-range]').forEach((root) => {
    const rMin = root.querySelector('[data-price-range-min]');
    const rMax = root.querySelector('[data-price-range-max]');
    const nMin = root.querySelector('[data-price-min]');
    const nMax = root.querySelector('[data-price-max]');
    const fill = root.querySelector('[data-price-fill]');
    if (!rMin || !rMax || !fill) return;

    const max = Number(rMax.max) || 0;

    const paint = () => {
      const lo = Number(rMin.value);
      const hi = Number(rMax.value);
      fill.style.left = max ? `${(lo / max) * 100}%` : '0%';
      fill.style.right = max ? `${100 - (hi / max) * 100}%` : '0%';
    };

    // The handles must not cross, in either direction.
    const sync = (from) => {
      let lo = Number(rMin.value);
      let hi = Number(rMax.value);
      if (lo > hi) {
        if (from === 'min') { hi = lo; rMax.value = hi; } else { lo = hi; rMin.value = lo; }
      }
      nMin.value = lo;
      nMax.value = hi;
      paint();
    };

    rMin.addEventListener('input', () => sync('min'));
    rMax.addEventListener('input', () => sync('max'));

    // Typing in the boxes drives the handles back.
    const fromNumbers = () => {
      const lo = Math.min(Math.max(Number(nMin.value) || 0, 0), max);
      const hi = Math.min(Math.max(Number(nMax.value) || max, 0), max);
      rMin.value = Math.min(lo, hi);
      rMax.value = Math.max(lo, hi);
      paint();
    };
    nMin.addEventListener('change', fromNumbers);
    nMax.addEventListener('change', fromNumbers);

    paint();

    // An untouched pair still reads 0.00; submitting price.lte=0 would match
    // nothing, so drop those params rather than filtering the grid to empty.
    root.closest('form')?.addEventListener('submit', () => {
      [nMin, nMax].forEach((el) => {
        if (Number(el.value) === 0) el.disabled = true;
      });
    });
  });

  /* The drawer hangs below the sticky header rather than covering it.
     Measure the header's BOTTOM, not its height: the announcement bar sits
     above the header and is still on screen at scroll 0, so at the top of the
     page the header ends lower than its own height. Body scroll is locked while
     the drawer is open, so the value cannot go stale until it closes — taking
     it on open (and on resize) is enough, no scroll listener needed. */
  const header = document.querySelector('.shopify-section.section-header') ||
                 document.querySelector('[data-header]');
  const setDrawerTop = () => {
    if (!header) return;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--collection-drawer-top', `${bottom}px`);
  };
  setDrawerTop();
  window.addEventListener('resize', setDrawerTop);

  /* ── Filter drawer ──────────────────────────────────────────────────────
     Same model as the cart drawer: the panel never leaves the layout, so open
     and close are just .is-open — the CSS transitions run both ways with no
     timers. The only wait left is Apply Filters, see below. */
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SLIDE_MS =
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--collection-drawer-slide-out')
    ) || 520;

  const setDrawer = (d, open) => {
    if (open) setDrawerTop();
    const toggle = d.querySelector('[data-filters-toggle]');
    // The panel goes visibility:hidden once closed; don't strand focus in it.
    if (!open && d.querySelector('.collection__filters-panel')?.contains(document.activeElement)) toggle?.focus();
    d.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
  };

  const openMenus = () =>
    document.querySelectorAll('.collection__filters.is-open, .collection__sort[open]');

  const closeMenu = (d) =>
    d.classList.contains('collection__filters') ? setDrawer(d, false) : (d.open = false);

  document.querySelectorAll('[data-filters]').forEach((d) => {
    d.querySelector('[data-filters-toggle]')?.addEventListener('click', () =>
      setDrawer(d, !d.classList.contains('is-open')));

    // The close button sits inside the panel, so the outside-click handler
    // below deliberately ignores it — it has to close the drawer itself.
    d.querySelector('[data-filters-close]')?.addEventListener('click', () => setDrawer(d, false));

    // Apply Filters is a plain GET submit: the browser swaps the page at once,
    // so the drawer would vanish in one frame. Hold navigation until the slide
    // finishes. form.submit() skips the submit event, so the price handler
    // above (which ran on this first event) is not re-run.
    const form = d.querySelector('form.collection__filters-panel');
    form?.addEventListener('submit', (e) => {
      if (REDUCED.matches) return;
      e.preventDefault();
      setDrawer(d, false);
      setTimeout(() => form.submit(), SLIDE_MS);
    });
  });

  /* A click on the scrim targets the menu wrapper itself (the scrim is its
     ::before), so testing against the panel and the trigger — not the
     wrapper — is what makes scrim clicks count as "outside". */
  document.addEventListener('click', (e) => {
    openMenus().forEach((d) => {
      const panel = d.querySelector('.collection__filters-panel, .collection__sort-menu');
      const trigger = d.querySelector('summary, [data-filters-toggle]');
      if (!panel?.contains(e.target) && !trigger?.contains(e.target)) closeMenu(d);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') openMenus().forEach(closeMenu);
  });

})();

/* Infinite scroll.

   The numbered pagination nav doubles as the sentinel and as the no-JS
   fallback: JS hides it, then loads the next page as it comes into view. The
   next page is fetched as a whole document and only its grid items are kept —
   no Section Rendering API call, because the paginated URL already returns
   exactly the page we want. */
(() => {
  document.querySelectorAll('[data-infinite-scroll]').forEach((nav) => {
    const spinner = nav.parentElement?.querySelector('[data-more-loading]');
    const grid  = nav.closest('.collection__results')?.querySelector('.product-grid');
    if (!grid || !('IntersectionObserver' in window)) return;

    nav.style.visibility = 'hidden';  // still occupies the sentinel's space
    let busy = false;

    const load = async () => {
      const url = nav.dataset.nextUrl;
      if (busy || !url) return;
      busy = true;
      if (spinner) spinner.hidden = false;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        grid.append(...doc.querySelectorAll('.collection__results .product-grid > li'));

        nav.dataset.nextUrl = doc.querySelector('[data-infinite-scroll]')?.dataset.nextUrl || '';

        // The appended cards carry [data-reveal] and land at opacity 0 with
        // nothing watching them until the reveal observer rescans.
        document.dispatchEvent(new Event('on:reveal:scan'));
      } catch {
        nav.style.visibility = '';  // hand the numbered links back as a fallback
        nav.dataset.nextUrl = '';
      } finally {
        busy = false;
        if (spinner) spinner.hidden = true;
      }
    };

    // Re-arming after each load matters: if the appended page is shorter than
    // the root margin the nav never leaves the observer's box, so there is no
    // new intersection to fire and the scroll would stall one page in.
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      load().then(() => {
        if (!nav.dataset.nextUrl) { io.disconnect(); return; }
        io.unobserve(nav);
        io.observe(nav);
      });
    }, { rootMargin: '600px 0px' });
    io.observe(nav);
  });
})();
